export type Calendar = { name: string; year: number; type: string; listOfDays?: string[] };
export type Contract = { name: string; startDate: string; endDate: string; totalHours: number };

const CAL_KEY = 'times.calendars';
const CONTRACT_KEY = 'times.contracts';

function read<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as T[];
  } catch {
    return [];
  }
}

function write<T>(key: string, data: T[]) {
  localStorage.setItem(key, JSON.stringify(data));
}

export async function getCalendars(): Promise<Calendar[]> {
  return read<Calendar>(CAL_KEY);
}

export async function saveCalendar(cal: Calendar): Promise<boolean> {
  if (!cal || !cal.name) return false;
  const items = read<Calendar>(CAL_KEY).filter(c => !c.name || c.name.toLowerCase() !== cal.name.toLowerCase());
  items.push(cal);
  write(CAL_KEY, items);
  return true;
}

export async function deleteCalendar(name: string): Promise<boolean> {
  const items = read<Calendar>(CAL_KEY);
  const before = items.length;
  const remaining = items.filter(c => c.name.toLowerCase() !== name.toLowerCase());
  if (remaining.length === before) return false;
  write(CAL_KEY, remaining);
  return true;
}

export async function calendarExists(name: string): Promise<boolean> {
  const items = read<Calendar>(CAL_KEY);
  return items.some(c => c.name.toLowerCase() === name.toLowerCase());
}

export async function getContracts(): Promise<Contract[]> {
  return read<Contract>(CONTRACT_KEY);
}

export async function saveContract(ct: Contract): Promise<boolean> {
  if (!ct || !ct.name) return false;
  const items = read<Contract>(CONTRACT_KEY).filter(c => !c.name || c.name.toLowerCase() !== ct.name.toLowerCase());
  items.push(ct);
  write(CONTRACT_KEY, items);
  return true;
}

export async function deleteContract(name: string): Promise<boolean> {
  const items = read<Contract>(CONTRACT_KEY);
  const before = items.length;
  const remaining = items.filter(c => c.name.toLowerCase() !== name.toLowerCase());
  if (remaining.length === before) return false;
  write(CONTRACT_KEY, remaining);
  return true;
}

function toYMD(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDateOnly(s: string): Date {
  // Accepts YYYY-MM-DD or full ISO; create local Date at midnight
  const parts = s.split('T')[0].split('-').map(Number);
  return new Date(parts[0], (parts[1] ?? 1) - 1, parts[2] ?? 1);
}

function startOfWeekMonday(d: Date): Date {
  const day = d.getDay(); // 0=Sun .. 6=Sat
  const delta = (day - 1 + 7) % 7;
  const copy = new Date(d);
  copy.setDate(d.getDate() - delta);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function maxDate(a: Date, b: Date) { return a > b ? a : b; }
function minDate(a: Date, b: Date) { return a < b ? a : b; }

export async function getProjection(name: string, enabledCalendarNames?: string[]): Promise<any> {
  const contracts = read<Contract>(CONTRACT_KEY);
  const contract = contracts.find(c => c.name === name);
  if (!contract) return null;

  const cals = read<Calendar>(CAL_KEY);

  const enabledSet = enabledCalendarNames ? new Set(enabledCalendarNames.map(x => x.toLowerCase())) : null;

  const allWorkDays = new Set<string>();
  const allNonWorkDays = new Set<string>();

  for (const c of cals.filter(x => x.type === 'WorkDays')) {
    if (enabledSet && !enabledSet.has((c.name ?? '').toLowerCase())) continue;
    for (const d of c.listOfDays ?? []) {
      allWorkDays.add(d.slice(0,10));
    }
  }
  for (const c of cals.filter(x => x.type === 'NonWorkDays')) {
    if (enabledSet && !enabledSet.has((c.name ?? '').toLowerCase())) continue;
    for (const d of c.listOfDays ?? []) {
      allNonWorkDays.add(d.slice(0,10));
    }
  }

  const start = parseDateOnly(contract.startDate);
  const end = parseDateOnly(contract.endDate);
  if (start > end) throw new Error('StartDate must be before EndDate');

  const lines: Array<any> = [];
  let remaining = Number(contract.totalHours);
  let thusFar = 0;

  let weekStart = startOfWeekMonday(start);
  while (weekStart <= end) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const winStart = maxDate(weekStart, start);
    const winEnd = minDate(weekEnd, end);

    const workingDays: string[] = [];
    for (let d = new Date(winStart); d <= winEnd; d.setDate(d.getDate() + 1)) {
      const key = toYMD(new Date(d));
      if (allWorkDays.has(key)) workingDays.push(key);
    }

    const nonWorkingDays: string[] = [];
    for (let d = new Date(winStart); d <= winEnd; d.setDate(d.getDate() + 1)) {
      const key = toYMD(new Date(d));
      if (allNonWorkDays.has(key)) nonWorkingDays.push(key);
    }

    const actual = workingDays.filter(x => !nonWorkingDays.includes(x));

    const capacity = actual.length * 8.0;
    const hours = Math.min(Math.max(0.0, remaining), capacity);
    remaining -= hours;
    thusFar += hours;

    lines.push({
      weekStart: toYMD(weekStart),
      hours: Math.round(hours * 100) / 100,
      balance: Math.round(Math.max(0.0, remaining) * 100) / 100,
      sum: thusFar
    });

    weekStart = new Date(weekStart);
    weekStart.setDate(weekStart.getDate() + 7);
  }

  return {
    contract: { name: contract.name, startDate: contract.startDate, endDate: contract.endDate, totalHours: contract.totalHours },
    lines
  };
}
